import React, { FunctionComponent, FormEvent } from 'react';
import ReCAPTCHA, { type ReCAPTCHAProps } from 'react-google-recaptcha';
import { PlanPurchaseForm } from '../../../types/companyRegisterForm';
import { Loading } from '../../atoms/loading';
import { CompanyRegisterPlanFormTextBox } from '../../molecules/companyRegisterPlanFormTextBox';
import { CompanyRegisterPlanFormZipCode } from '../../molecules/companyRegisterPlanFormZipCode';
import { PLAN_GROUPS } from './constants';
import { NextPlanInfo } from '../../../types/companyAccount';
import { PlanCard } from '../../molecules/planCard';

type Props = {
  planPurchase: PlanPurchaseForm;
  isLoading: boolean;
  isSubmitting: boolean;
  recaptchaToken: string | null;
  recaptchaSiteKey: string;
  planOptions: NextPlanInfo[];
  handleChange: (section: 'company' | 'userAuth' | 'planPurchase', field: string, value: string | boolean| number) => void;
  onSubmit: (e: FormEvent) => void;
  onRecaptcha: (token: string | null) => void;
};

export const Presentation: FunctionComponent<Props> = ({
  planPurchase,
  isLoading,
  isSubmitting,
  recaptchaToken,
  recaptchaSiteKey,
  planOptions,
  handleChange,
  onSubmit,
  onRecaptcha,
}) => {
  const ReCAPTCHAWidget = ReCAPTCHA as unknown as React.ComponentType<ReCAPTCHAProps>;
  return (
    <>
      {isLoading && (
        <div className="overall-loading">
          <Loading />
        </div>
      )}
      <div className="form-wrapper">
        <form className="plan_purchase_form p-rate-plan" onSubmit={onSubmit}>
          <h2 className="p-rate-plan__heading">プランを選択</h2>
          <p className="p-rate-plan__addinfo">
            本契約の有効期限は、アカウント開設（ご契約開始）日より有効です。<br />
            ただし、期間満了の前日までに解約の申し出がないときは、継続し以後も同様となります。<br /><br />
            例）1/1に6ヶ月プランを利用開始の場合、7/1までのご契約となります
          </p>
          <div className="p-rate-plan__inner">
            {/** --- プランリスト --- */}
            {PLAN_GROUPS.map((group) => (
              <div className="p-rate-plan__inner-block" key={group.title}>
                <p className="p-rate-plan__input-text-title">{group.title}</p>
                <div className="p-rate-plan__cards">
                  {group.indexes.map((index) => {
                    const plan = planOptions[index]
                    if (!plan) return null
                    return (
                      <PlanCard
                        key={index}
                        isPlanOptions={true}
                        isTrial={false}
                        nextPlanInfo={plan}
                        selected={plan.contractPlanId == planPurchase.contractPlanId}
                        onClickPlanCard={() =>
                          handleChange('planPurchase', 'contractPlanId', plan.contractPlanId as number)
                        }
                      />
                    )
                  })}
                </div>
              </div>
            ))}

            {/* 招待コードは使わないため一時コメントアウト、確定したら削除してOK */}
            {/* <div className="p-rate-plan__input-text">
              <span>招待コード(任意)</span>
              <input
                type="text"
                id="user_company_attributes_plan_purchase_invitation_code"
                value={planPurchase.invitationCode}
                onChange={(e) => handleChange('planPurchase', 'invitationCode', e.target.value)}
              />
            </div> */}
          </div>

          <h2 className="p-rate-plan__heading">お支払い方法</h2>
          <div className="p-rate-plan__inner">

            {/* 使用する予定がないため一時的にコメントアウト、確定したら削除してOK */}
            {/* <ul>
              <li>
                <label className="p-rate-plan__agreement">
                  <input
                    type="radio"
                    checked={planPurchase.paymentMethodId === 1}
                    onChange={() => handleChange('planPurchase', 'paymentMethodId', 1)}
                  />
                  <span></span>
                  <span>請求書一括支払い</span>
                </label>
              </li>
            </ul>
            <p className="p-rate-plan__notice">※クレジットカード支払いは準備中です。</p> */}

            <p className="p-rate-plan__addinfo-pay">
              請求書をお送りしますので、ご利用料金を一括でお支払いただきます。<br />
              また、お支払い期日はアカウント開設（ご契約開始）日の翌月末とさせていただきます。<br /><br />
              例）ベーシックプラン（6ヶ月利用）の場合 : 7万円 × 6ヶ月 ＝ 42万円（税抜）<br /><br />
              領収書の発行はおこなっておりません。<br />
              振込みの際のお客様控え、払い込みの際の払い込み金受領証をもってかえさせていただきます。
            </p>
          </div>

          <h2 className="p-rate-plan__heading">請求先情報</h2>
          <div className="p-rate-plan__inner">
            <CompanyRegisterPlanFormTextBox
              section="planPurchase"
              name="companyName"
              divClassName="form-group column"
              labelName="会社名"
              required={true}
              inputId="company_name_input"
              inputClassName="form-control"
              value={planPurchase.companyName}
              onChange={handleChange}
            />
            <CompanyRegisterPlanFormTextBox
              section="planPurchase"
              name="department"
              divClassName="form-group column"
              labelName="担当部署"
              required={true}
              inputId="department_input"
              inputClassName="form-control"
              value={planPurchase.department}
              onChange={handleChange}
            />
            <CompanyRegisterPlanFormTextBox
              section="planPurchase"
              name="inCharge"
              divClassName="form-group column"
              labelName="ご担当者名"
              required={true}
              inputId="in_charge_input"
              inputClassName="form-control"
              value={planPurchase.inCharge}
              onChange={handleChange}
            />
            <CompanyRegisterPlanFormZipCode
              zipCode01={planPurchase.zipCode01}
              zipCode02={planPurchase.zipCode02}
              onChange={handleChange}
            />
            <CompanyRegisterPlanFormTextBox
              section="planPurchase"
              name="address01"
              divClassName="form-group column"
              labelName="会社住所（番地まで）"
              required={true}
              inputId="address01_input"
              inputClassName="form-control"
              value={planPurchase.address01}
              onChange={handleChange}
            />
            <CompanyRegisterPlanFormTextBox
              section="planPurchase"
              name="address02"
              divClassName="form-group column"
              labelName="会社住所（ビル名・部屋番号）"
              required={false}
              inputId="address02_input"
              inputClassName="form-control"
              value={planPurchase.address02}
              onChange={handleChange}
            />
            <CompanyRegisterPlanFormTextBox
              section="planPurchase"
              name="email"
              divClassName="form-group column margin"
              labelName="請求書送付先メールアドレス"
              required={true}
              inputId="email_input"
              inputClassName="form-control"
              value={planPurchase.email}
              onChange={handleChange}
              hintMessage="※メール以外を希望の場合は会社アドレスを入力の上営業担当にお申し付けください"
            />
            <CompanyRegisterPlanFormTextBox
              section="planPurchase"
              name="emailSubFirst"
              divClassName="form-group column margin"
              labelName="上記以外で受け取りたいメールアドレス1"
              required={false}
              inputId="email_sub_first_input"
              inputClassName="form-control"
              value={planPurchase.emailSubFirst}
              onChange={handleChange}
            />
            <CompanyRegisterPlanFormTextBox
              section="planPurchase"
              name="emailSubSecond"
              divClassName="form-group column margin"
              labelName="上記以外で受け取りたいメールアドレス2"
              required={false}
              inputId="email_sub_second_input"
              inputClassName="form-control"
              value={planPurchase.emailSubSecond}
              onChange={handleChange}
            />
          </div>

          <h2 className="p-rate-plan__heading">利用規約等の確認</h2>
          <div className="p-rate-plan__inner">
            <div className="p-rate-plan__notices">
              <p>
                お申し込み後すぐに、ベーシックプラン/ミニマムプラン/ショートプラン/成功報酬プランをご利用いただけます。<br />
                お申し込み後の解約は対応しかねますので、予めご了承くださいませ。
              </p>
              <p>
                お預かりしたお客様の個人情報は、弊社からお客様へのご連絡のために使用します。<br />
                法令に定める場合を除き、第三者に対して個人情報の提供・開示する事はありません。
              </p>
            </div>

            <p className="p-rate-plan__agreement-link">
              <a href="/companies/terms" target="_blank">利用規約（掲載企業向け）</a>、
              <a href="/companies/terms/success" target="_blank">利用規約（掲載企業向け｜成功報酬プラン）</a>、
              <a href="/mag/guideline/" target="_blank">コンテンツガイドライン</a>、
              <a href="/tokusyohou" target="_blank">特商法</a>
              をご確認ください。
            </p>

            <div className="p-rate-plan__checkbox">
              <label className="p-rate-plan__checkbox--contents">
                <input
                  type="checkbox"
                  checked={planPurchase.agreement}
                  onChange={(e) => handleChange('planPurchase', 'agreement', e.target.checked)}
                  className="p-rate-plan__box"
                />
                <span>上記の利用規約の内容を確認のうえ同意します</span>
              </label>
            </div>
          </div>

          <div className="p-rate-plan__recaptcha">
            <ReCAPTCHAWidget
              sitekey={recaptchaSiteKey}
              onChange={onRecaptcha}
              hl="ja"
            />
          </div>
          <button
            type="submit"
            className="button-agree center"
            disabled={!planPurchase.agreement || !recaptchaToken || isSubmitting}
          >
            {isSubmitting ? '通信中...' : '同意して登録する'}
          </button>
        </form>
      </div>
    </>
  );
};
